import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Slider } from '@mui/material';
import { styled } from "@mui/system";

import { simulateData } from "../../../utils/api";
import { palette } from "../../../styles/palette";
import {
  setPageLoading,
  setSimulatedVolumeOverlayData,
  setSimulationPercentage,
  setShowErrorModal,
  setErrorModalMessage,
} from "../../../reducers/app";
import { setShowManifestLocationStops } from "../../../reducers/map";
import {
  defaultOverlayDataSelector,
  simulationPercentageSelector,
  volumeOverlayDateSelector,
  overlayTimeBandSelector,
  simulatedVolumeOverlayDataSelector,
} from "../../../reducers/app/selector";
import { showManifestLocationStopsSelector } from "../../../reducers/map/selector";
import {
  depotCodeSelector,
  manifestLocationsSelector,
} from "../../../reducers/depot/selector";
import { setUpSimulationParcelList } from "../../../utils/map";
import { ArrowIcon } from "../../../assets/icons/SidebarArrowIcon";
import { formatDate } from "../../../utils/formatDate";
import { SLIDER_VALUES } from "../../../constants/sliderValues";
import { OUTER } from "../../../constants/layers";
import { SwitchButton } from "./SwitchButton";
import "../Sidebar.css";
import { SHOW_MANIFEST_LOCATION_STOPS } from "../../../constants/switchButtons";

const StyledSlider = styled(Slider)({
  width: "80%",
  '& .MuiSlider-thumb': {
    color: palette.dpdRed,
  },
  '& .MuiSlider-track': {
    color: palette.dpdRed,
  },
  '& .MuiSlider-rail': {
    color: palette.typography.black.solid,
  },
  '& .MuiSlider-mark': {
    color: palette.dpdRed,
  },
  '& .MuiSlider-markLabel': {
    color: palette.lightGrey,
  },
  '& .MuiSlider-markLabelActive': {
    color: palette.lightGrey,
  },
});

export const SimulationSideBar = () => {
  const [errorMsg, setErrorMsg] = useState("");

  const percentageValue = useSelector(simulationPercentageSelector);
  const depotCode = useSelector(depotCodeSelector);
  const volumeOverlayDate = useSelector(volumeOverlayDateSelector);
  const simulatedDataList = useSelector(simulatedVolumeOverlayDataSelector);
  const defaultOverlayDataList = useSelector(defaultOverlayDataSelector);
  const overlayTimeBand = useSelector(overlayTimeBandSelector);
  const showManifestLocationStops = useSelector(
    showManifestLocationStopsSelector
  );
  const manifestLocations = useSelector(manifestLocationsSelector);

  const dispatch = useDispatch();

  const DistrictsList = () => {
    const districts = setUpSimulationParcelList(
      OUTER,
      defaultOverlayDataList,
      simulatedDataList,
      overlayTimeBand,
      "list"
    );

    return districts
      .sort((current, next) => next.stops - current.stops)
      .map((postcode, pIndex) => {
        return (
          <div
            key={pIndex}
            className="simulatedDataListRow"
            style={{
              gridTemplateColumns: `${
                simulatedDataList !== null && simulatedDataList.percentage !== 0
                  ? "repeat(4,1fr)"
                  : "repeat(2,1fr)"
              }`,
            }}
          >
            <div>{postcode.value}:</div>
            <div>{postcode.stops}</div>
            {simulatedDataList !== null &&
              simulatedDataList.percentage !== 0 && (
                <>
                  <ArrowIcon />
                  <div>{postcode.simStops}</div>
                </>
              )}
          </div>
        );
      });
  };

  const sliderMarks = SLIDER_VALUES.MARKS;
  let currentValue = sliderMarks[0];
  const sliderLength =
    (sliderMarks[sliderMarks.length - 1] - sliderMarks[0]) / 10;
  const sliderValues = [];

  for (let index = 0; index <= sliderLength; index++) {
    sliderValues.push({
      value: index,
      label: sliderMarks.includes(currentValue) ? `${currentValue}%` : null,
      popup: `${currentValue}%`,
      percentage: currentValue / 100,
    });
    currentValue = currentValue + 10;
  }

  // Always ensures zero is the default value regardless of the array (i.e, has the negative values)
  const getIndexOfZero = sliderValues.findIndex(
    (value) => value.percentage === 0
  );

  return (
    <>
      <div className="simulationContainer">
        {depotCode && volumeOverlayDate && defaultOverlayDataList.length > 0 ? (
          <>
            <p>Select value(%) to simulate:</p>
            <StyledSlider
              key={`${volumeOverlayDate}-sidebar-slider`}
              marks={sliderValues}
              max={sliderValues.length - 1}
              step={1}
              defaultValue={getIndexOfZero}
              valueLabelDisplay={"auto"}
              onChangeCommitted={(e, value) => {
                dispatch(
                  setSimulationPercentage(sliderValues[value].percentage)
                );
              }}
              valueLabelFormat={(value) => {
                return sliderValues[value].popup;
              }}
            />
            <button
              className="sideBarButton"
              onClick={async () => {
                dispatch(setPageLoading(true));
                try {
                  if (percentageValue === 0) {
                    // The default file for percentage value 0 already gets made when you select the overlay date so no need to run simulation on it again
                    dispatch(setSimulatedVolumeOverlayData(null));
                  } else {
                    const simulatedData = await simulateData(
                      depotCode,
                      formatDate(volumeOverlayDate),
                      percentageValue
                    );
                    if (simulatedData.data.length === 0) {
                      setErrorMsg(
                        `No Data Found for Date: ${formatDate(
                          volumeOverlayDate
                        )} and Depot: ${depotCode}`
                      );
                    }
                    simulatedData.data.percentage = percentageValue;
                    dispatch(setSimulatedVolumeOverlayData(simulatedData.data));
                  }
                  dispatch(setPageLoading(false));
                } catch (e) {
                  dispatch(setShowErrorModal(true));
                  dispatch(setErrorModalMessage("Simulating Data"));
                  dispatch(setPageLoading(false));
                }
              }}
            >
              {percentageValue === 0
                ? "Show original values"
                : `Simulate ${Math.abs(percentageValue * 100)}% ${
                    percentageValue > 0 ? "increase" : "decrease"
                  }`}
            </button>
          </>
        ) : (
          <div className="sideBarSimulateButtonAltText">
            Please select a depot and date first
          </div>
        )}
      </div>
      <div style={{ height: "100vh" }}>
        {defaultOverlayDataList.length > 0 ? (
          <div className="simulatedDataListContainer">
            {manifestLocations && manifestLocations.length > 0 && (
              <SwitchButton
                title={SHOW_MANIFEST_LOCATION_STOPS.title}
                matchingReduxState={showManifestLocationStops}
                onStateChange={() => {
                  dispatch(
                    setShowManifestLocationStops(!showManifestLocationStops)
                  );
                }}
                values={SHOW_MANIFEST_LOCATION_STOPS.values}
              />
            )}
            <div className="simulatedDataListText">
              {depotCode} - {formatDate(volumeOverlayDate, "MMMM Do YYYY")}
            </div>
            <div className="simulationSimulatedDataContainer">
              Original stops
              {simulatedDataList !== null &&
                simulatedDataList.percentage !== 0 && (
                  <>
                    <ArrowIcon />
                    {simulatedDataList.percentage * 100}%
                  </>
                )}
            </div>
            <div className="simulatedDataList">
              <DistrictsList />
            </div>
          </div>
        ) : (
          <div className="simulationErrorMsg">{errorMsg}</div>
        )}
      </div>
    </>
  );
};
